import { PdCourse } from "../pd-courses/models";
import { HistoryItem, PracticeReview } from "../practice-reviews";
import { User } from "../users";
import { DateTime } from "luxon";

export enum DecisionTypeCode {
  Reviewer = "Reviewer",
  Manager = "Manager",
  Director = "Director",
  Committee = "Committee"
}

export interface DecisionType {
  id: number;
  typeCode: DecisionTypeCode;
  name: string;
}

export interface Decision extends HistoryItem {
  decisionType: DecisionType;

  pprpProgramContinuance: YesNoNa | null;
  pprpProgramContinuanceExplanation: string | null;
  cpabParticipating: YesNoNa | null;
  cpdCompliance: YesNoNa | null;
  cpdComplianceExplanation: string | null;
  pliCompliance: YesNoNa | null;
  pliComplianceExplanation: string | null;

  complianceZone: ComplianceZone | null;
  followUpDate: string | null;
  nonComplyZoneNote: string | null;

  assignedPdCourses: AssignedPdCourse[];
  directedPdDueDate: string | null;

  motionStandard: MotionParagraph | null;
  motionPprpProgramReview: MotionParagraph | null;
  motionPd: MotionParagraph | null;
  motionCpab: MotionParagraph | null;
  motionCic: MotionParagraph | null;

  descriptionOfIntendedAction: string | null;
  legacyAdditionalComments: string | null;
  additionalCommentsForMinutes: string | null;
  additionalCommentsForDecisionLetter: string | null;
  appendedParagraphNames: string[];

  reviewerMemo: string | null;
  managerMemo: string | null;
  directorsHighlights: string | null;
  directorsSummaryOfFirmResponse: string | null;

  isComplete: boolean;

  signedOff: boolean;
  signedOffDate: string | null;
  signedOffByUser: User | null;

  hasOutstandingDiscussChanges: boolean;
  discussChangesCompleted: boolean;

  requiresFullScopeFollowUp: boolean | null;
  fullScopeFollowUpComments: string | null;

  monitoringRequired: boolean;
  monitoringFrequencyInDays: number | null;
  monitoringCount: number | null;

  recommendationsToCommittee: string | null;

  practiceReview?: PracticeReview;
}

export interface DecisionInput {
  id?: number;
  practiceReviewId: number;
  decisionTypeCode: DecisionTypeCode;

  isComply: boolean | null;
  nonComplyFinancial: boolean;
  nonComplyAudit: boolean;
  nonComplyReview: boolean;
  nonComplyQualityManagement: boolean;
  nonComplyTax: boolean;
  nonComplyCompilation: boolean;
  nonComplyOther: boolean;
  nonComplyOtherSpecified: string | null;

  complianceZoneId: number | null;
  followUpDate: string | null;
  nonComplyZoneNote: string | null;

  pprpProgramContinuance: YesNoNa | null;
  pprpProgramContinuanceExplanation: string | null;
  cpabParticipating: YesNoNa | null;
  cpdCompliance: YesNoNa | null;
  cpdComplianceExplanation: string | null;
  pliCompliance: YesNoNa | null;
  pliComplianceExplanation: string | null;

  recommendedPdCourseIds: number[];
  directedPdCourses: {
    courseId: number;
    isCompleted: boolean;
  }[];
  directedPdDueDate: string | null;

  motionStandardId: number | null;
  motionPprpProgramReviewId: number | null;
  motionPdId: number | null;
  motionCpabId: number | null;
  motionCicId: number | null;

  descriptionOfIntendedAction: string | null;
  additionalCommentsForMinutes: string | null;
  additionalCommentsForDecisionLetter: string | null;

  reviewerMemo: string | null;
  managerMemo: string | null;
  directorsHighlights: string | null;
  directorsSummaryOfFirmResponse: string | null;

  requiresFullScopeFollowUp: boolean | null;
  fullScopeFollowUpComments: string | null;

  recommendationsToCommittee: string | null;
}

export interface AssignedPdCourse {
  id: number;
  pdCourse: PdCourse;
  isDirected: boolean;
  mustBeTakenBy: string | null;
  isCompleted: boolean;
  isExempt: boolean;
  exemptReason: string | null;
  name: string | null;
  productCode: string | null;
  topicName: string | null;
  proofOfAttendanceFormId: number | null;
  signedProofOfAttendanceId: number | null;
}

export interface ComplianceZone {
  id: number;
  name: string;
  zoneCode: string;
  isNonComplyZone: boolean;
  requiresNote: boolean;
  requiresResponse: boolean;
  maySupplyFollowUpDate: boolean;
  isForPprpProgramReviews: boolean;
  requiresFollowUpScope: boolean;
}

export enum YesNoNa {
  Yes = "Yes",
  No = "No",
  Na = "Na"
}

export interface SignOffChecklistItem {
  id: number;
  decisionType: DecisionType;
  questionText: string;
  sortOrder: number;
  displayTemplateName: string | null;
  isChecked: boolean;
}

export enum MotionTypeCode {
  Standard = "Standard",
  PPRPProgram = "PPRPProgram",
  PD = "PD",
  CPAB = "CPAB",
  CIC = "CIC"
}

export interface MotionType {
  id: number;
  name: string;
  typeCode: MotionTypeCode;
}

export interface MotionParagraph {
  id: number;
  motionType: MotionType;
  shortDescription: string;
  motionCode: string;
  isActive: boolean;
  isComply: boolean;
  isNonComply: boolean;
  isInUse: boolean;
  sortOrder: number;
  paragraphForMinutes: string;
  paragraphForDecisionLetter: string;
  paragraphForPprpProgramReviewSummaryDataSheet: string;
  motionPprpStatusRecommendedId: number | null;
}

export interface MotionParagraphInput {
  id?: number;
  motionTypeId: number;
  shortDescription: string;
  motionCode: string;
  isActive: boolean;
  isComply: boolean;
  isNonComply: boolean;
  sortOrder: number;
  paragraphForMinutes: string;
  paragraphForDecisionLetter: string;
  paragraphForPprpProgramReviewSummaryDataSheet: string;
}

export interface DecisionAdditionalParagraph {
  id: number;
  name: string;
  isActive: boolean;
  forRegistrationCommitteeItems: boolean;
  paragraphForMinutes: string;
  paragraphForDecisionLetter: string;
}

export interface DecisionAdditionalParagraphInput {
  id?: number;
  name: string;
  isActive: boolean;
  forRegistrationCommitteeItems: boolean;
  paragraphForMinutes: string;
  paragraphForDecisionLetter: string;
}

export interface DecisionFormValues {
  id?: number;
  practiceReviewId: number;
  decisionTypeCode: DecisionTypeCode;

  isComply: boolean | null;
  nonComplyFinancial: boolean;
  nonComplyAudit: boolean;
  nonComplyReview: boolean;
  nonComplyQualityManagement: boolean;
  nonComplyTax: boolean;
  nonComplyCompilation: boolean;
  nonComplyOther: boolean;
  nonComplyOtherSpecified: string | null;

  complianceZoneId: number | null;
  followUpDate: DateTime | null;
  nonComplyZoneNote: string | null;

  pprpProgramContinuance: YesNoNa | null;
  pprpProgramContinuanceExplanation: string | null;
  cpabParticipating: YesNoNa | null;
  cpdCompliance: YesNoNa | null;
  cpdComplianceExplanation: string | null;
  pliCompliance: YesNoNa | null;
  pliComplianceExplanation: string | null;

  recommendedPdCourseIds: number[];
  directedPdCourses: { courseId: number; isCompleted: boolean }[];
  directedPdDueDate: DateTime | null;

  motionStandardId: number;
  motionPprpProgramReviewId: number;
  motionPdId: number;
  motionCpabId: number;
  motionCicId: number;

  reviewerMemo: string | null;
  managerMemo: string | null;
  descriptionOfIntendedAction: string | null;
  additionalCommentsForMinutes: string | null;
  additionalCommentsForDecisionLetter: string | null;
  directorsHighlights: string | null;
  directorsSummaryOfFirmResponse: string | null;
  appendedParagraphNames: string[];

  discussDecisionChange: boolean;
  cancelDecisionChange: boolean;

  requiresFullScopeFollowUp: string;
  fullScopeFollowUpComments: string | null;

  recommendationsToCommittee: string | null;
}
